import { useState, useContext, useCallback, useEffect } from 'react';
import axios from 'axios';
import moment from 'moment';
import { PeFilterContext } from '../Contexts/PeFilterContext';
import { TableDataContext } from '../Contexts/TableDataContext';
import { UserContext } from '../Contexts/UserContext';
import { getPEDealsApi } from '../Config/config';
import Constants from '../Constants';
import { getCategoryWiseGlobalSearchIds } from '../Components/useGlobalSearch';

// Custom hook for chart analytics functionality
export const useChartAnalytics = (menuType, pathname) => {
  // const [showChartPopup, setShowChartPopup] = useState(false);
  const [currentChartData, setCurrentChartData] = useState(null);
  const [isChartLoading, setIsChartLoading] = useState(false);
  const [currentShowBy, setCurrentShowBy] = useState('');
  const [interval, setInterval] = useState('yearly');
  const [period, setPeriod] = useState('CY');

  const { showChartPopup, setShowChartPopup } = useContext(PeFilterContext)
  
  const {
    refineSearchFilters,
    monthPickerValue,
    SubFirmType,
    accessControl,
    groupId
  } = useContext(PeFilterContext);
  
  const { globalSearchResult, setGlobalSearchResult } = useContext(TableDataContext);
  const { getToken, logout } = useContext(UserContext);

  const pathKey = pathname.split("/").slice(0, 3).join("/");

  const isFromGlobalSearch = () =>
    refineSearchFilters[pathname]?.globalSearch?.length > 0 ? true : false;

  // Fallback chart data structure
  const fallbackChartData = {
    "success": true,
    "message": "Loading chart data...",
    "data": {
      "summary": {
        "total_periods": 0,
        "totals": {
          "deal_count": 0
        },
        "group_totals": {}
      },
      "dataset": []
    }
  };

  // Function to get current show_by value based on applied filters
  const getShowByValue = () => {
    const filters = refineSearchFilters[pathKey];
    const pathnameFilters = refineSearchFilters[pathname];
    
    const appliedFilters = [];

    appliedFilters.push('year');

    if(isFromGlobalSearch()){
      appliedFilters.push('company')
      return appliedFilters[0]
    }
    
    // Check pathKey filters
    if (filters?.sector?.length > 0) appliedFilters.push('sector');
    if (filters?.countries?.length > 0) appliedFilters.push('country');
    if (filters?.region?.length > 0) appliedFilters.push('region');
    if (filters?.yearFounded?.length > 0) appliedFilters.push('year_founded');
    if (filters?.state?.length > 0) appliedFilters.push('state');
    if (filters?.city?.length > 0) appliedFilters.push('city');
    if (filters?.investorType?.length > 0) appliedFilters.push('investor_type');
    if (filters?.investorHeadquarters?.length > 0) appliedFilters.push('investor_headquaters');
    if (filters?.investor_headquaters_city?.length > 0) appliedFilters.push('investor_headquaters_city');
    if (filters?.firmType?.length > 0) appliedFilters.push('firm_type');
    if (filters?.firm_Sub_type?.length > 0) appliedFilters.push('firm_Sub_type');
    if (filters?.tags?.length > 0) appliedFilters.push('tag');
    if (filters?.ladvisors?.length > 0) appliedFilters.push('ladvisor');
    if (filters?.tadvisors?.length > 0) appliedFilters.push('tadvisor');
    if (filters?.investor_id?.length > 0) appliedFilters.push('investor');
    if (filters?.target_company?.length > 0) appliedFilters.push('company');
    if (filters?.industry?.length > 0) appliedFilters.push('industry');

    // Check pathname filters
    if (pathnameFilters?.companyType?.length > 0) appliedFilters.push('company_type');
    if (pathnameFilters?.dealRange?.length > 0) appliedFilters.push('deal_range');
    if (pathnameFilters?.dealType?.length > 0) appliedFilters.push('deal_type');
    if (pathnameFilters?.dealsInvolving?.length > 0) appliedFilters.push('deals_involving');
    if (pathnameFilters?.exitStatus?.length > 0) appliedFilters.push('exit_status');
    if (pathnameFilters?.round?.length > 0) appliedFilters.push('round');
    if (pathnameFilters?.stage?.length > 0) appliedFilters.push('stage');
    if (pathnameFilters?.syndication?.length > 0) appliedFilters.push('syndication');
    if (pathnameFilters?.valuations?.length > 0) appliedFilters.push('valuation');
    if (pathnameFilters?.investorSaleInipo?.length > 0) appliedFilters.push('investor_sale');
    if (pathnameFilters?.vcFunded?.length > 0) appliedFilters.push('vc_fund');
    if (pathnameFilters?.exited?.length > 0) appliedFilters.push('exited');
    if (pathnameFilters?.incubationStatus?.length > 0) appliedFilters.push('incubation_status');
    if (pathnameFilters?.excludeDefunctCos?.length > 0) appliedFilters.push('defunct');
    if (pathnameFilters?.exitType?.length > 0) appliedFilters.push('exit_type');
    if (pathnameFilters?.returnMultiple?.length > 0) appliedFilters.push('return_multiple');
    if (pathnameFilters?.firmType?.length > 0) appliedFilters.push('firm_type');
    
    // Return first available filter or empty string if none applied
    return appliedFilters.length > 0 ? appliedFilters[0] : '';
  };

  // Function to get available filter options
  const getAvailableShowByOptions = () => {
    const filters = refineSearchFilters[pathKey];
    const pathnameFilters = refineSearchFilters[pathname];
    
    const availableOptions = [];
    availableOptions.push('year');

    if(isFromGlobalSearch()){
      availableOptions.push('company')
      return availableOptions;
    }

    // Add pathKey filter options
    if (filters?.industry?.length > 0) availableOptions.push('industry');
    if (filters?.sector?.length > 0) availableOptions.push('sector');
    if (filters?.countries?.length > 0) availableOptions.push('country');
    if (filters?.region?.length > 0) availableOptions.push('region');
    if (filters?.yearFounded?.length > 0) availableOptions.push('year_founded');
    if (filters?.state?.length > 0) availableOptions.push('state');
    if (filters?.city?.length > 0) availableOptions.push('city');
    if (filters?.investorType?.length > 0) availableOptions.push('investor_type');
    if (filters?.investorHeadquarters?.length > 0) availableOptions.push('investor_headquaters');
    if (filters?.investor_headquaters_city?.length > 0) availableOptions.push('investor_headquaters_city');
    if (filters?.firmType?.length > 0) availableOptions.push('firm_type');
    if (filters?.firm_Sub_type?.length > 0) availableOptions.push('firm_Sub_type');
    if (filters?.tags?.length > 0) availableOptions.push('tag');
    if (filters?.ladvisors?.length > 0) availableOptions.push('ladvisor');
    if (filters?.tadvisors?.length > 0) availableOptions.push('tadvisor');
    if (filters?.investor_id?.length > 0) availableOptions.push('investor');
    if (filters?.target_company?.length > 0) availableOptions.push('company');
    
    // Add pathname filter options
    if (pathnameFilters?.companyType?.length > 0) availableOptions.push('company_type');
    if (pathnameFilters?.dealRange?.length > 0) availableOptions.push('deal_range');
    if (pathnameFilters?.dealType?.length > 0) availableOptions.push('deal_type');
    if (pathnameFilters?.dealsInvolving?.length > 0) availableOptions.push('deals_involving');
    if (pathnameFilters?.exitStatus?.length > 0) availableOptions.push('exit_status');
    if (pathnameFilters?.round?.length > 0) availableOptions.push('round');
    if (pathnameFilters?.stage?.length > 0) availableOptions.push('stage');
    if (pathnameFilters?.syndication?.length > 0) availableOptions.push('syndication');
    if (pathnameFilters?.valuations?.length > 0) availableOptions.push('valuation');
    if (pathnameFilters?.investorSaleInipo?.length > 0) availableOptions.push('investor_sale');
    if (pathnameFilters?.vcFunded?.length > 0) availableOptions.push('vc_fund');
    if (pathnameFilters?.exited?.length > 0) availableOptions.push('exited');
    if (pathnameFilters?.incubationStatus?.length > 0) availableOptions.push('incubation_status');
    if (pathnameFilters?.excludeDefunctCos?.length > 0) availableOptions.push('defunct');
    if (pathnameFilters?.exitType?.length > 0) availableOptions.push('exit_type');
    if (pathnameFilters?.returnMultiple?.length > 0) availableOptions.push('return_multiple');
    if (pathnameFilters?.firmType?.length > 0) availableOptions.push('firm_type');

    return availableOptions;
  };

  // Function to get category and type based on menu
  const getCategoryAndType = (menu) => {
    const category =
      menu == "pe" || menu == "vc"
        ? "investments"
        : menu == "exit-pe-vc" ||
          menu == "exit-ma-pe-vc" ||
          menu == "exit-ma-vc" ||
          menu == "exit-pm-pe-vc" ||
          menu == "exit-pm-vc" ||
          menu == "vc_exit"
        ? "exits"
        : menu == "backedIpos-pe-vc" || menu == "backedIpos-vc"
        ? "ipos"
        : menu == "angel" || menu == "vc-angel"
        ? "angel"
        : menu == "incubation" || menu == "vc-incubation"
        ? "incubation"
        : "";

    const type =
      menu == "pe"
        ? "pe_vc_investment"
        : menu == "vc"
        ? "vc_investment"
        : menu == "exit-pe-vc"
        ? "pe_vc_exit"
        : menu == "exit-ma-pe-vc"
        ? "pe_ma_exit"
        : menu == "vc_exit"
        ? "vc_exit"
        : menu == "exit-pm-pe-vc"
        ? "pe_pm_exit"
        : menu == "exit-pm-vc"
        ? "vc_pm_exit"
        : menu == "exit-ma-vc"
        ? "vc_ma_exit"
        : menu == "backedIpos-pe-vc"
        ? "pe_vc_ipo"
        : menu == "backedIpos-vc"
        ? "vc_ipo"
        : "";

    return { category, type };
  };

  // Function to get sub type master id based on menu
  const getSubTypeMasterId = (menu) => {
    return menu == "pe"
      ? Constants.subCategoryIds.pe_investments_id
      : menu == "exit-pe-vc"
      ? Constants.subCategoryIds.pe_exits_id
      : menu == "exit-ma-pe-vc"
      ? Constants.subCategoryIds.pe_exits_id
      : menu == "exit-pm-pe-vc"
      ? Constants.subCategoryIds.pe_exits_id
      : menu == "angel"
      ? Constants.subCategoryIds.pe_angel_id
      : menu == "incubation"
      ? Constants.subCategoryIds.pe_incubation_id
      : Constants.subCategoryIds.pe_investments_id;
  };

  // Function to build API payload
  const buildApiPayload = async (showByValue, intervalValue = interval, periodValue = period) => {
    const { category, type } = getCategoryAndType(menuType);

    const categoryToCollectionMap = {
      "investments": "pe_investment",
      "exits": "pe_exit", 
      "ipos": "pe_ipo",
      "angel": "angel",
      "incubation": "incubation"
    };
    const gsCategory = categoryToCollectionMap[category]

    // Extract PE and VC IDs from SubFirmType
    const peIds = SubFirmType
      ?.filter(firm => firm.name?.includes('(PE)'))
      ?.map(firm => firm.id) || [];

    const vcIds = SubFirmType
      ?.filter(firm => firm.name?.includes('(VC)'))
      ?.map(firm => firm.id) || [];

    // Extract IDs from refineSearchFilters
    const refineIds = refineSearchFilters[pathKey]?.firm_Sub_type?.map(firm => firm.id) || [];

    // Filter PE and VC IDs from refineIds
    const filteredPeIds = refineIds.filter(id => peIds.includes(id));
    const filteredVcIds = refineIds.filter(id => vcIds.includes(id));

    const dealIds = isFromGlobalSearch () ? await getCategoryWiseGlobalSearchIds(globalSearchResult, setGlobalSearchResult, gsCategory) : [];

    return {
      category: category,
      type: type,
      from: isFromGlobalSearch() ? "gs" : "",
      date_range: {
        from_date: moment(monthPickerValue?.[0] || moment().startOf("month").subtract(12, "months")).format("YYYY-MM-DD"),
        to_date: moment(monthPickerValue?.[1] || moment().endOf("month")).format("YYYY-MM-DD"),
      },
      tab_category: "company",
      industry: refineSearchFilters[pathKey]?.industry?.length > 0 
        ? refineSearchFilters[pathKey]?.industry?.map((value) => value.id) 
        : [],
      sector: refineSearchFilters[pathKey]?.sector?.map(
        (value) => value.name
      ) || [],
      deals_operator: category === "incubation" ? 'or' : category === "angel" ? refineSearchFilters[pathname]?.deals_operator : refineSearchFilters[pathKey]?.deals_operator,
      deal_ids: isFromGlobalSearch() ? Object.keys(dealIds).length > 0 ? dealIds[gsCategory] : [0] : [],
      company_ids: isFromGlobalSearch() ? []
        : refineSearchFilters[pathKey]?.target_company?.map(company => company.id) || refineSearchFilters[pathname]?.companyIds?.at(0)?.value,
      ladvisor_ids: category === "angel" || category === "incubation" ? [] : refineSearchFilters[pathKey]?.ladvisors?.map(acq => acq.id) || [],
      tadvisor_ids: category === "angel" || category === "incubation" ? [] : refineSearchFilters[pathKey]?.tadvisors?.map(acq => acq.id) || [],
      investor_ids: category === "angel" || category === "incubation" ? refineSearchFilters[pathname]?.investor_id?.map(acq => acq.id) || [] : refineSearchFilters[pathKey]?.investor_id?.map(acq => acq.id) || [],
      stage: refineSearchFilters[pathname]?.stage?.length > 0 ? refineSearchFilters[pathname]?.stage?.map(
        (value) => value.id
      ) : accessControl?.pe_stage?.id,
      round: refineSearchFilters[pathname]?.round?.map(
        (value) => value.name
      ) || [],
      investor_id: refineSearchFilters[pathname]?.companyIds?.at(0)?.investorId,
      valuation: refineSearchFilters[pathname]?.valuations?.map((valuation) => valuation.value),
      investor_headquaters: refineSearchFilters[pathKey]?.investorHeadquarters?.map((inv) => inv.id),
      investor_headquaters_city: refineSearchFilters[pathKey]?.investor_headquaters_city?.map((inv) => inv.name),
      deal_type: refineSearchFilters[pathname]?.dealType?.map(
        (type) => type.id
      ),
      company_type: refineSearchFilters[pathname]?.companyType?.map((type) => type.id),
      year_founded: refineSearchFilters[pathKey]?.yearFounded?.at(0) && {
        from: refineSearchFilters[pathKey]?.yearFounded[0]?.name,
        to: refineSearchFilters[pathKey]?.yearFounded[1]?.name,
      },
      syndication: refineSearchFilters[pathname]?.syndication?.at(0)?.value
        ? [refineSearchFilters[pathname]?.syndication?.at(0)?.value]
        : [],
      deals_involving: refineSearchFilters[pathname]?.dealsInvolving?.map((deal) => deal.value),
      country: refineSearchFilters[pathKey]?.countries?.length > 0 ? refineSearchFilters[pathKey]?.countries?.map(
        (country) => country.id
      ) : accessControl?.pe_countries?.id,
      investor_type: refineSearchFilters[pathKey]?.investorType?.map((inv) => inv.id),
      investor_sale: refineSearchFilters[pathname]?.investorSaleInipo?.map((inv) => inv.id),
      region: refineSearchFilters[pathKey]?.region?.length > 0 ? refineSearchFilters[pathKey]?.region?.map(
        (region) => region.id
      ) : accessControl?.pe_regions?.id,
      state: refineSearchFilters[pathKey]?.state?.length > 0 ? refineSearchFilters[pathKey]?.state?.map(
        (state) => state.id
      ) : accessControl?.pe_states?.id,
      city: refineSearchFilters[pathKey]?.city?.length > 0 ? refineSearchFilters[pathKey]?.city?.map(
        (city) => city.id
      ) : accessControl?.pe_cities?.id,
      deal_range: refineSearchFilters?.[pathname]?.hasOwnProperty("dealRange") &&
        refineSearchFilters[pathname]?.dealRange?.length >= 2
          ? {
              from: refineSearchFilters[pathname].dealRange[0].name,
              to: refineSearchFilters[pathname].dealRange[1].name,
            }
          : {
              from: accessControl?.pe_deal_range?.from,
              to: accessControl?.pe_deal_range?.to,
            },
      exit_status: category === "exits" || category === "ipos" ? refineSearchFilters[pathKey]?.exitStatus?.map((status) => status.id) : refineSearchFilters[pathname]?.exitStatus?.map((status) => status.id),
      exit_type: refineSearchFilters[pathname]?.exitType?.map(
        (type) => type.id
      ),
      exited: refineSearchFilters[pathname]?.exited?.map(
        (exit) => exit.id
      ),
      vc_fund: refineSearchFilters[pathname]?.vcFunded?.map(
        (vc) => vc.id
      ),
      incubation_status: refineSearchFilters[pathname]?.incubationStatus?.map((vc) => vc.id),
      defunct: refineSearchFilters[pathname]?.excludeDefunctCos?.at(0)?.value ? 1 : 0,
      firm_type: category === "incubation"
        ? refineSearchFilters[pathname]?.firmType?.map((firm) => firm.id)
        : {
            firm_type: category === "angel"
              ? refineSearchFilters[pathname]?.firmType?.map((firm) => firm.id)
              : refineSearchFilters[pathKey]?.firmType?.map((firm) => firm.id),
            pe_firm_sub_type: filteredPeIds.length === peIds.length ? [] : filteredPeIds,
            vc_firm_sub_type: filteredVcIds.length === vcIds.length ? [] : filteredVcIds,
          },
      return_multiple: refineSearchFilters[pathname]?.returnMultiple?.at(0) && {
        from: refineSearchFilters[pathname]?.returnMultiple?.at(0)?.name,
        to: refineSearchFilters[pathname]?.returnMultiple?.at(1)?.name,
      },
      ...(showByValue && { show_by: showByValue }),
      interval: intervalValue,
      period: periodValue,
      isChart: true,
      view_by: 'both',
      tag: (refineSearchFilters[pathKey]?.tags?.length ?? 0) != 0
        ? {
            operator: refineSearchFilters[pathKey]?.operator,
            data: refineSearchFilters[pathKey]?.tags?.map(
              (tag) => tag.name
            ),
          }
        : accessControl?.pe_tags.id?.length > 0 ? { 
            operator: 1,
            data: accessControl?.pe_tags?.name
          } : null,
      group: {
        group_id: groupId?.myList?.map((id) => id.group_id)?.toString() ?? null,
        sub_type_master_id: groupId?.myList?.length > 0 ? getSubTypeMasterId(menuType) : null,
      },
    };
  };

  // Function to fetch chart data
  const handleChartIconClick = async () => {
    try {
      const currentShowByValue = getShowByValue();
      setCurrentShowBy(currentShowByValue);
      
      setIsChartLoading(true);
      setCurrentChartData(null);
      // setShowChartPopup(true);
      
      let apiShowByValue = currentShowByValue;
      if (currentShowByValue === 'multiple_filters') {
        const availableOptions = getAvailableShowByOptions();
        apiShowByValue = availableOptions[0] || '';
      }

      const payload = await buildApiPayload(apiShowByValue);

      const response = await axios(getPEDealsApi, {
        method: "POST",
        data: payload,
        headers: {
          "Content-Type": "application/json",
          Authorization: getToken(),
        },
      });

      if (response?.data?.success === true && response?.data?.message === "Chart data generated successfully") {
        setCurrentChartData(response.data);
        setIsChartLoading(false);
      } else {
        console.error('Unexpected response format:', response?.data);
        setCurrentChartData(fallbackChartData);
        setIsChartLoading(false);
      }
    } catch (error) {
      console.error('Error fetching chart data:', error);
      if (error?.response?.status == Constants.sessionTerminatedStatus 
        || error?.response?.status == Constants.InvalidTokenStatus
      ){
        logout();
      }
      setCurrentChartData(fallbackChartData);
      setIsChartLoading(false);
    }
  };

  useEffect(() => {
    handleChartIconClick()
  }, [refineSearchFilters[pathname], monthPickerValue])

  // Function to handle show by change
  const handleShowByChange = async (newShowBy) => {
    setCurrentShowBy(newShowBy);
    setIsChartLoading(true);
    
    try {
      const payload = await buildApiPayload(newShowBy);

      const response = await axios(getPEDealsApi, {
        method: "POST",
        data: payload,
        headers: {
          "Content-Type": "application/json",
          Authorization: getToken(),
        },
      });

      if (response?.data?.success === true && response?.data?.message === "Chart data generated successfully") {
        setCurrentChartData(response.data);
        setIsChartLoading(false);
      } else {
        console.error('Unexpected response format:', response?.data);
        setCurrentChartData(fallbackChartData);
        setIsChartLoading(false);
      }
    } catch (error) {
      console.error('Error fetching chart data:', error);
      setCurrentChartData(fallbackChartData);
      setIsChartLoading(false);
    }
  };

  const handlePeriodChange = async (newPeriod) => {
    setPeriod(newPeriod);
    setIsChartLoading(true);
    try {
      const payload = await buildApiPayload(currentShowBy, interval, newPeriod);
      const response = await axios(getPEDealsApi, {
        method: "POST",
        data: payload,
        headers: { "Content-Type": "application/json", Authorization: getToken() },
      });
      if (response?.data?.success === true && response?.data?.message === "Chart data generated successfully") {
        setCurrentChartData(response.data);
      } else {
        setCurrentChartData(fallbackChartData);
      }
    } catch (error) {
      console.error('Error fetching chart data:', error);
      setCurrentChartData(fallbackChartData);
    } finally {
      setIsChartLoading(false);
    }
  };

  const handleIntervalChange = async (newInterval) => {
    setInterval(newInterval);
    setIsChartLoading(true);
    try {
      const payload = await buildApiPayload(currentShowBy, newInterval);
      const response = await axios(getPEDealsApi, {
        method: "POST",
        data: payload,
        headers: { "Content-Type": "application/json", Authorization: getToken() },
      });
      if (response?.data?.success === true && response?.data?.message === "Chart data generated successfully") {
        setCurrentChartData(response.data);
      } else {
        setCurrentChartData(fallbackChartData);
      }
    } catch (error) {
      console.error('Error fetching chart data:', error);
      setCurrentChartData(fallbackChartData);
    } finally {
      setIsChartLoading(false);
    }
  };

  return {
    showChartPopup,
    setShowChartPopup,
    currentChartData,
    setCurrentChartData,
    isChartLoading,
    setIsChartLoading,
    currentShowBy,
    fallbackChartData,
    getAvailableShowByOptions,
    handleChartIconClick,
    handleShowByChange,
    interval,
    handleIntervalChange,
    period,
    handlePeriodChange
  };
};

export default useChartAnalytics;